/**
 * Personality & Delight Utilities
 * Fun messages, easter eggs, and playful copy
 */

// ============================================================================
// LOADING MESSAGES
// ============================================================================

const LOADING_MESSAGES = {
  default: [
    "Shuffling the deck...",
    "Consulting the oracle...",
    "Warming up the cards...",
    "Gathering the players...",
    "Preparing something special...",
    "Almost ready...",
    "Just a moment...",
  ],
  game: [
    "Dealing the cards...",
    "Setting up the table...",
    "Preparing your hand...",
    "The game is afoot...",
    "Let the games begin...",
    "Rolling for initiative...",
  ],
  connecting: [
    "Establishing connection...",
    "Finding your friends...",
    "Syncing up...",
    "Connecting the dots...",
    "Reaching across the tubes...",
  ],
  thinking: [
    "Pondering the possibilities...",
    "Consulting the experts...",
    "Crunching the numbers...",
    "Deep in thought...",
    "Processing brilliance...",
  ],
  waiting: [
    "Waiting for players...",
    "Gathering the crew...",
    "Rounding up the gang...",
    "The more the merrier...",
    "Your friends are on their way...",
  ],
  // Game-specific loading contexts
  psychiatrist: [
    "The doctor will see you now...",
    "Diagnosing the situation...",
    "Preparing prescriptions...",
    "Consulting the DSM-V...",
    "Setting up the couch...",
  ],
  heist: [
    "Casing the vault...",
    "Gathering the crew...",
    "Checking security cameras...",
    "The alarm is set...",
    "Time to steal the show...",
  ],
  debate: [
    "Preparing arguments...",
    "Setting up the podiums...",
    "The judge is ready...",
    "Clear your throat...",
    "Time to make your case...",
  ],
  hivemind: [
    "Syncing the hive...",
    "Connecting minds...",
    "Telepathy loading...",
    "Great minds think alike...",
    "One brain, many players...",
  ],
  codebreakers: [
    "Generating codebook...",
    "Encrypting messages...",
    "Assembling the grid...",
    "Setting up the field...",
    "Agents standing by...",
  ],
  liarshand: [
    "Rolling the dice...",
    "Can you spot the bluff?",
    "Poker faces ready...",
    "Trust no one...",
    "The game of deception begins...",
  ],
  bluffalo: [
    "Shuffling definitions...",
    "Loading the dictionary...",
    "Preparing ridiculous answers...",
    "Time to get creative...",
    "The truth is out there...",
  ],
  caw: [
    "Shuffling the deck...",
    "Dealing white cards...",
    "The czar awaits...",
    "Gathering terrible answers...",
    "Preparing for hilarity...",
  ],
  sickday: [
    "Calling in sick...",
    "Preparing excuses...",
    "The boss is listening...",
    "Time to get creative...",
    "Playing hooky...",
  ],
  peerreview: [
    "Gathering feedback...",
    "The critics assemble...",
    "Ratings incoming...",
    "Preparing for judgment...",
    "Time to rate and review...",
  ],
};

export type LoadingContext = keyof typeof LOADING_MESSAGES;

/**
 * Get a random loading message for context
 */
export function getLoadingMessage(context: LoadingContext = "default"): string {
  const messages = LOADING_MESSAGES[context];
  return messages[Math.floor(Math.random() * messages.length)];
}

/**
 * Get loading messages that cycle through
 */
export function* loadingMessageCycle(context: LoadingContext = "default") {
  const messages = [...LOADING_MESSAGES[context]];
  let index = 0;

  while (true) {
    yield messages[index];
    index = (index + 1) % messages.length;
  }
}

// ============================================================================
// SUCCESS MESSAGES
// ============================================================================

const SUCCESS_MESSAGES = {
  win: [
    "Victory is yours! 🏆",
    "Champion! 👑",
    "Absolutely crushed it!",
    "The crowd goes wild!",
    "Legendary performance!",
  ],
  correct: [
    "Nailed it! ✨",
    "Spot on!",
    "That's the one!",
    "Brilliant!",
    "You've got it!",
  ],
  submit: [
    "Locked in!",
    "Submitted!",
    "Your move is made!",
    "Done and dusted!",
  ],
  join: [
    "Welcome to the party! 🎉",
    "You're in!",
    "Let's gooo!",
    "The gang's all here!",
  ],
};

export type SuccessContext = keyof typeof SUCCESS_MESSAGES;

export function getSuccessMessage(context: SuccessContext = "submit"): string {
  const messages = SUCCESS_MESSAGES[context];
  return messages[Math.floor(Math.random() * messages.length)];
}

// ============================================================================
// ERROR MESSAGES
// ============================================================================

const ERROR_MESSAGES = {
  connection: [
    "Oops! Lost connection. Reconnecting...",
    "The tubes are clogged. Hold tight...",
    "Connection hiccup! We're on it.",
  ],
  timeout: [
    "That took too long! Try again?",
    "Time's up on that one.",
    "We waited, but nothing came back.",
  ],
  generic: [
    "Something went sideways.",
    "Well, that didn't work.",
    "Houston, we have a problem.",
    "Gremlins in the system!",
  ],
  notFound: [
    "Couldn't find that one.",
    "It's not here... yet?",
    "404: Fun not found.",
  ],
};

export type ErrorContext = keyof typeof ERROR_MESSAGES;

export function getErrorMessage(context: ErrorContext = "generic"): string {
  const messages = ERROR_MESSAGES[context];
  return messages[Math.floor(Math.random() * messages.length)];
}

// ============================================================================
// ENCOURAGEMENT
// ============================================================================

const ENCOURAGEMENT = [
  "You've got this!",
  "Believe in yourself!",
  "Great choice!",
  "Bold move!",
  "I like your style!",
  "Interesting strategy...",
  "Playing 4D chess, I see!",
  "The odds are ever in your favor!",
];

export function getEncouragement(): string {
  return ENCOURAGEMENT[Math.floor(Math.random() * ENCOURAGEMENT.length)];
}

// ============================================================================
// WAITING FOR OTHERS
// ============================================================================

const WAITING_PROMPTS = [
  "Still thinking...",
  "Any moment now...",
  "The suspense!",
  "Worth the wait...",
  "Great things take time...",
  "Patience, grasshopper...",
];

export function getWaitingPrompt(): string {
  return WAITING_PROMPTS[Math.floor(Math.random() * WAITING_PROMPTS.length)];
}

// ============================================================================
// PLAYER JOIN/LEAVE
// ============================================================================

export function getJoinMessage(name: string): string {
  const templates = [
    `${name} has entered the arena!`,
    `${name} joins the party! 🎉`,
    `Welcome, ${name}!`,
    `${name} is ready to play!`,
    `The legend ${name} has arrived!`,
  ];
  return templates[Math.floor(Math.random() * templates.length)];
}

export function getLeaveMessage(name: string): string {
  const templates = [
    `${name} has left the game.`,
    `${name} stepped away.`,
    `Goodbye, ${name}!`,
    `${name} vanished into the mist...`,
  ];
  return templates[Math.floor(Math.random() * templates.length)];
}

// ============================================================================
// EASTER EGGS
// ============================================================================

const KONAMI_CODE = [
  "ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown",
  "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight",
  "b", "a",
];

/**
 * Track Konami code input
 */
export function createKonamiTracker(onSuccess: () => void) {
  let index = 0;

  return (key: string) => {
    if (key === KONAMI_CODE[index]) {
      index++;
      if (index === KONAMI_CODE.length) {
        index = 0;
        onSuccess();
      }
    } else {
      index = 0;
    }
  };
}

// ============================================================================
// TIME-BASED GREETINGS
// ============================================================================

export function getTimeGreeting(): string {
  const hour = new Date().getHours();

  if (hour < 5) return "Burning the midnight oil?";
  if (hour < 12) return "Good morning!";
  if (hour < 17) return "Good afternoon!";
  if (hour < 21) return "Good evening!";
  return "Night owl mode activated!";
}

// ============================================================================
// STREAK & COMBO MESSAGES
// ============================================================================

export function getStreakMessage(streak: number): string {
  if (streak < 2) return "";
  if (streak === 2) return "Double!";
  if (streak === 3) return "Triple! 🔥";
  if (streak === 4) return "Quad! 🔥🔥";
  if (streak === 5) return "PENTA! 🔥🔥🔥";
  if (streak < 10) return `${streak}x Streak! 🔥`;
  return `UNSTOPPABLE! ${streak}x 🔥🔥🔥🔥`;
}

export function getComboMessage(combo: number): string {
  if (combo < 2) return "";
  if (combo === 2) return "Combo!";
  if (combo === 3) return "Nice combo!";
  if (combo === 5) return "COMBO MASTER!";
  if (combo < 10) return `${combo}x Combo!`;
  return `LEGENDARY ${combo}x COMBO!`;
}
